import { useState } from 'react';
import { useMsal } from '@azure/msal-react';
import ReportsComponent from '../../components/Reports/Reports';
import CompetencyPeriodSelector from '../../components/CompetencyPeriodSelector/CompetencyPeriodSelector';
import ReportFilterComponent from '../../components/Reports/ReportFilterComponent';

import { ICompetencyMgmtPeriod } from '../../model/CompetencyMgmtPeriod.model';

type ReportDef = {
    key: string;
    label: string;
    title: string;
    needsPeriod?: boolean;
    needsFilter?: boolean;    
    adminOnly?: boolean;
    endpoint: string | ((period: ICompetencyMgmtPeriod) => string);
};

const appendFilterParams = (url: string, filters: Record<string, string>): string => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
    });
    const queryString = params.toString();
    return queryString ? `${url}?${queryString}` : url;
};

const REPORTS: ReportDef[] = [
    {
        key: 'EmployeeProgCountsBySkills',
        label: 'All Bookmark Counts by Skill',
        title: 'All Bookmark Counts by Skill',        
        endpoint: '/api/reports/EmployeeProgCountsBySkills',
        needsFilter: true,
        adminOnly: true
    },
   
    {
        key: 'CompetencyDeltas',
        label: 'Competency Deltas',
        title: 'Competency Deltas',
        needsPeriod: true,
        adminOnly: true,
        endpoint: (period) => `/api/reports/CompetencyDeltas/${period.competencyMgmtPeriodId}`,
    },
   
    {
        key: 'allCompetencies',
        label: 'All Employee Competencies',
        title: 'All Employee Competencies',
        needsPeriod: true,
        needsFilter: false,
        adminOnly: true,
        endpoint: (period) => `/api/reports/AllCompetenciesByPeriod/${period.competencyMgmtPeriodId}`
    }
];

const Reports = () => {
    const { instance } = useMsal();
    const roles = (instance.getActiveAccount()?.idTokenClaims?.roles ?? []) as string[];
    const isAdmin = roles.indexOf('CareerFramework.Admin') > -1;

    const visibleReports = REPORTS.filter(r => !r.adminOnly || isAdmin);

    const [selectedReport, setSelectedReport] = useState<string>(visibleReports[0].key);
    const [period, setPeriod] = useState<ICompetencyMgmtPeriod | null>(null);
    const [filters, setFilters] = useState<Record<string, string>>({});
    const [activeEndpoint, setActiveEndpoint] = useState<string | null>(null);

    const report = visibleReports.find(r => r.key === selectedReport) ?? visibleReports[0];

    const baseEndpoint = typeof report.endpoint === 'function'
        ? (period ? report.endpoint(period) : null)
        : report.endpoint;

    const endpoint = baseEndpoint && report.needsFilter
        ? appendFilterParams(baseEndpoint, filters)
        : baseEndpoint;

    const handleReportChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedReport(e.target.value);
        setFilters({});
        setFilters({});
        setActiveEndpoint(null);
    };

    const handlePeriodChange = (p: ICompetencyMgmtPeriod) => {
        setPeriod(p);
        setActiveEndpoint(null);
    };

    const handleFilterChange = (next: Record<string, string>) => {
        setFilters(next);
        setActiveEndpoint(null);
    };

    return <>
        <div className="max-width">            
            <div className="dis-flex dis-flex-rows dis-flex-aln-center gap--50">
                <div>
                    <label htmlFor="report-select" className="m-r-1 fnt-wght-bold">Select a Report: </label>
                    <select
                        id="report-select"
                        value={report.key}
                        onChange={handleReportChange}
                    >
                        {visibleReports.map(r => (
                            <option key={r.key} value={r.key}>{r.label}</option>
                        ))}
                    </select>
                </div>
                <button
                    type="button"
                    className="btn-primary"
                    disabled={!endpoint}
                    onClick={() => setActiveEndpoint(endpoint)}
                >
                    View Report
                </button>
            </div>
            <div >
           
            {report.needsPeriod && (
                <div className="m-t-1">
                    <CompetencyPeriodSelector onPeriodChange={handlePeriodChange} />
                </div>
            )}

            {report.needsFilter && (
                <div className="m-t-1">
                    <ReportFilterComponent onFilterChange={handleFilterChange} />
                </div>
            )}
            </div>           
        </div>

        {activeEndpoint && <ReportsComponent title={report.title} endpoint={activeEndpoint} />}
    </>;
};

export default Reports;
